import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table } from 'app-components';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { EMPTY_VALUE } from '../../constants';
import { IHouseholdMember, IHouseholdMembersSectionProps } from '../../types';
import { safeFormatDate } from '../../utils';

// ============================================================================
// Component
// ============================================================================

const HouseholdMembersSection: React.FC<IHouseholdMembersSectionProps> = ({ application }) => {
    const intl = useIntl();
    const {
        primaryContactFirstName,
        primaryContactLastName,
        applicants,
    } = application;

    // Use ?? to handle null (default = '' only works for undefined, not null)
    const primaryContact = `${primaryContactFirstName ?? ''} ${primaryContactLastName ?? ''}`.trim() || EMPTY_VALUE;

    // Map applicants to household members format
    const members: IHouseholdMember[] = applicants && applicants.length > 0 
        ? applicants.map(applicant => ({
            name: `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() || EMPTY_VALUE,
            memberId: applicant.bewellMemberId || EMPTY_VALUE,
            seekingCoverage: applicant.seekingCoverage != null
                ? intl.formatMessage({ id: applicant.seekingCoverage ? 'label.yes' : 'label.no' })
                : EMPTY_VALUE,
            primaryContact,
            ssn: socialSecurityNumberTools.maskSsnLastFour(applicant.ssnLastFour) || EMPTY_VALUE,
            dateOfBirth: safeFormatDate(applicant.dob, MM_SLASH_DD_SLASH_YYYY),
        })) 
        : [];

    const columns = useMemo(
        () => [
            {
                titleId: 'member.enrollment_list.premiumhistory.enrollee.name',
                key: 'name',
            },
            {
                titleId: 'historicalApp.member_ref_id',
                key: 'memberId',
            },
            {
                titleId: 'label.seekingCoverage',
                key: 'seekingCoverage',
            },
            {
                titleId: 'label.contact.primary',
                key: 'primaryContact',
                render: (data: IHouseholdMember) => (
                    <span>{data.primaryContact || EMPTY_VALUE}</span>
                ),
            },
            {
                titleId: 'member.enrollment_list.premiumhistory.enrollee.ssn',
                key: 'ssn',
            },
            {
                titleId: 'label.dob',
                key: 'dateOfBirth',
            },
        ],
        [],
    );

    return (
        <div>
            <h4 className="margin-top-0 margin-bottom-3">
                <FormattedMessage id="historical.member.applications.householdMembers" />
            </h4>
            <div className="historical-household-table">
                <Table
                    columns={columns}
                    data={members}
                    classNamesStr="household-members-table"
                />
            </div>
        </div>
    );
};

export default HouseholdMembersSection;

